import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { streamNdjson } from '../../services/streamNdjson';
import StreamProgressPanel from './StreamProgressPanel';

/**
 * FLOW : Reprise de cotisation FPM
 *
 * Étapes :
 *  1. Sélection de la période limite (année + mois)
 *  2. Prévisualisation (nb polices, nb cotisations à créer, montant total)
 *  3. Confirmation et lancement de la génération
 *
 * Pas de fichier à charger : le traitement se base sur les polices FPM
 * ACTIVE existantes et génère rétroactivement depuis leur effectiveDate
 * jusqu'à (year, month) inclus.
 */
const MONTHS = [
  { value: 1,  label: 'Janvier' },
  { value: 2,  label: 'Février' },
  { value: 3,  label: 'Mars' },
  { value: 4,  label: 'Avril' },
  { value: 5,  label: 'Mai' },
  { value: 6,  label: 'Juin' },
  { value: 7,  label: 'Juillet' },
  { value: 8,  label: 'Août' },
  { value: 9,  label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' }
];

const formatAmount = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' FCFA';
};

const BulkFpmGenerationFlow = ({ onCompleted }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [step, setStep] = useState('select'); // 'select' | 'preview' | 'done'
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null); // {current,total,policyNumber,contributionsCreated,paymentsCreated,totalAmount,errorsCount}

  const years = useMemo(() => {
    const list = [];
    for (let y = now.getFullYear() + 1; y >= 2015; y--) list.push(y);
    return list;
  }, [now]);

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await api.post('/payments/bulk-fpm-generation/preview', { year, month }, { timeout: 0 });
      setPreview(res.data?.data || null);
      setStep('preview');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Erreur lors de la prévisualisation';
      toast.error(msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    const initialTotal = preview?.policiesCount || 0;
    setProgress({
      current: 0, total: initialTotal, currentLabel: '',
      counters: { contribs: 0, payments: 0, amount: 0, errors: 0 }
    });
    try {
      const baseURL = api.defaults.baseURL || '';
      const finalResult = await streamNdjson({
        url: `${baseURL}/payments/bulk-fpm-generation?stream=1`,
        body: { year, month },
        onEvent: (evt) => {
          if (evt.type === 'start') {
            setProgress((p) => ({ ...(p || {}), total: evt.policiesTotal || p?.total || 0 }));
          } else if (evt.type === 'policy' || evt.type === 'policy-error') {
            setProgress((p) => ({
              ...(p || {}),
              current:      evt.current ?? p?.current ?? 0,
              total:        evt.total   ?? p?.total   ?? 0,
              currentLabel: evt.policyNumber || p?.currentLabel || '',
              counters: {
                contribs: evt.contributionsCreated ?? p?.counters?.contribs ?? 0,
                payments: evt.paymentsCreated      ?? p?.counters?.payments ?? 0,
                amount:   evt.totalAmount          ?? p?.counters?.amount   ?? 0,
                errors:   evt.errorsCount ?? (evt.type === 'policy-error'
                  ? (p?.counters?.errors || 0) + 1
                  : p?.counters?.errors || 0)
              }
            }));
          }
        }
      });

      setResult(finalResult);
      setStep('done');
      toast.success(
        finalResult
          ? `Génération terminée : ${finalResult.policiesProcessed}/${finalResult.policiesTotal} police(s), ${finalResult.contributionsCreated} cotisation(s).`
          : 'Génération terminée'
      );
      if (onCompleted) onCompleted();
    } catch (err) {
      toast.error(err?.message || 'Erreur lors de la génération');
    } finally {
      setRunning(false);
    }
  };

  const progressForPanel = progress ? {
    current:      progress.current,
    total:        progress.total,
    currentLabel: progress.currentLabel,
    metrics: [
      { label: 'Cotisations', value: progress.counters?.contribs || 0,            tone: 'blue' },
      { label: 'Paiements',   value: progress.counters?.payments || 0,            tone: 'indigo' },
      { label: 'Montant',     value: formatAmount(progress.counters?.amount || 0), tone: 'emerald' },
      { label: 'Erreurs',     value: progress.counters?.errors || 0,              tone: (progress.counters?.errors || 0) > 0 ? 'red' : 'gray' }
    ]
  } : null;

  const handleReset = () => {
    setPreview(null);
    setResult(null);
    setProgress(null);
    setStep('select');
  };

  // --- Rendu selon l'étape ---

  if (step === 'done' && result) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-start gap-4">
          <CheckCircleIcon className="h-8 w-8 text-green-600 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 mb-1">
              Génération FPM terminée
            </h3>
            <p className="text-sm text-green-800">
              Période limite : {MONTHS.find(m => m.value === result.endMonth)?.label} {result.endYear}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Polices traitées</div>
            <div className="text-2xl font-bold text-gray-900">
              {result.policiesProcessed}/{result.policiesTotal}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Cotisations créées</div>
            <div className="text-2xl font-bold text-blue-600">
              {result.contributionsCreated}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Paiements créés</div>
            <div className="text-2xl font-bold text-indigo-600">
              {result.paymentsCreated}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Montant total</div>
            <div className="text-2xl font-bold text-emerald-600">
              {formatAmount(result.totalAmount)}
            </div>
          </div>
        </div>

        {Array.isArray(result.errors) && result.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
              <ExclamationTriangleIcon className="h-5 w-5" />
              {result.errors.length} erreur(s)
            </div>
            <ul className="text-xs text-red-700 space-y-1 max-h-48 overflow-y-auto list-disc list-inside">
              {result.errors.slice(0, 50).map((e, i) => (
                <li key={i}>
                  {(e.policyNumber || '?')} — {e.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
          >
            Nouvelle reprise
          </button>
        </div>
      </div>
    );
  }

  if (step === 'preview' && preview) {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Période limite :</strong> {MONTHS.find(m => m.value === month)?.label} {year}
          </p>
          <p className="text-xs text-blue-700 mt-1">
            Le traitement va générer les cotisations + paiements FPM manquants
            entre la <em>date d'effet</em> de chaque police ACTIVE et la période
            limite. Les cotisations déjà existantes sont ignorées.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Polices FPM ACTIVE</div>
            <div className="text-2xl font-bold text-gray-900">{preview.policiesCount}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Cotisations à créer</div>
            <div className="text-2xl font-bold text-blue-600">{preview.totalContribs}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Montant total estimé</div>
            <div className="text-2xl font-bold text-emerald-600">
              {formatAmount(preview.totalAmount)}
            </div>
          </div>
        </div>

        {Array.isArray(preview.details) && preview.details.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700">
              Aperçu (top {preview.details.length} polices)
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Police</th>
                    <th className="px-3 py-2 text-right">Effet</th>
                    <th className="px-3 py-2 text-right">Mensualité</th>
                    <th className="px-3 py-2 text-right">Existantes</th>
                    <th className="px-3 py-2 text-right">À créer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.details.map((d) => (
                    <tr key={d.policyId}>
                      <td className="px-3 py-2">{d.policyNumber}</td>
                      <td className="px-3 py-2 text-right">
                        {new Date(d.effectiveDate).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-3 py-2 text-right">{formatAmount(d.monthlyAmount)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{d.existingContribs}</td>
                      <td className="px-3 py-2 text-right font-semibold text-blue-600">
                        {d.newContribs}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {preview.totalContribs === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            Aucune cotisation à créer pour cette période — tout est déjà couvert.
          </div>
        ) : null}

        {running && progressForPanel && (
          <StreamProgressPanel
            title="Génération en cours"
            progress={progressForPanel}
            barColor="emerald"
          />
        )}

        <div className="flex justify-between gap-3">
          <button
            onClick={handleReset}
            disabled={running}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium disabled:opacity-50"
          >
            Retour
          </button>
          <button
            onClick={handleRun}
            disabled={running || preview.totalContribs === 0}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {running && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {running ? 'Génération en cours...' : 'Lancer la génération'}
          </button>
        </div>
      </div>
    );
  }

  // Étape "select"
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        Choisis la <strong>période limite</strong> (année + mois inclus) jusqu'à
        laquelle la reprise doit générer les cotisations et paiements FPM, pour
        chaque police ACTIVE, depuis sa date d'effet.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mois</label>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handlePreview}
          disabled={loadingPreview}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {loadingPreview && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
          {loadingPreview ? 'Chargement...' : 'Prévisualiser'}
        </button>
      </div>
    </div>
  );
};

export default BulkFpmGenerationFlow;
